import axios from 'axios';
import { apiClient } from './apiClient';
import { useUserStore } from '../stores/user';

// CWE-922: token legacy que quedó en localStorage bajo la key de Zustand
// (`user-store`) de la versión anterior (que sí persistía el JWT). Se usa UNA
// vez para el puente de upgrade y luego se borra.
function readLegacyToken(): string | null {
	try {
		const raw = localStorage.getItem('user-store');
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		return parsed?.state?.token ?? null;
	} catch {
		return null;
	}
}

function clearLegacyToken() {
	try {
		const raw = localStorage.getItem('user-store');
		if (!raw) return;
		const parsed = JSON.parse(raw);
		if (parsed?.state?.token) {
			delete parsed.state.token;
			localStorage.setItem('user-store', JSON.stringify(parsed));
		}
	} catch {
		/* noop */
	}
}

// Una sola rehidratación por carga: React.StrictMode (dev) monta App dos veces
// y dispararía /me (y el logout del 401) duplicados.
let bootstrapping: Promise<void> | null = null;

/**
 * Rehidrata la sesión al cargar el app. Confía en la cookie HttpOnly mc_token
 * (via /me). Puente de upgrade: si hay un token legacy en localStorage y aún no
 * hay cookie, lo manda como X-Token → el backend autentica y setea la cookie
 * (UserValidation::__upgradeToCookie), migrando la sesión viva SIN re-login.
 */
export function bootstrapSession(): Promise<void> {
	if (!bootstrapping) bootstrapping = doBootstrapSession();
	return bootstrapping;
}

async function doBootstrapSession(): Promise<void> {
	const legacy = readLegacyToken();
	const config = legacy
		? { headers: { 'X-Token': `Bearer ${legacy}` } }
		: undefined;

	try {
		const res = await apiClient.get<unknown>('/me.json', config);
		const data = res.data as {
			success?: boolean;
			response?: { User?: Record<string, unknown>; exp?: number };
		};
		if (data?.success && data?.response?.User) {
			useUserStore.getState().setSession(data.response.User, data.response.exp);
			if (legacy) clearLegacyToken();
			return;
		}
	} catch (error) {
		// 401: el interceptor de apiClient ya ejecutó userLogout(); repetirlo
		// dispara un segundo POST /logout.json.
		if (axios.isAxiosError(error) && error.response?.status === 401) {
			if (legacy) clearLegacyToken();
			return;
		}
	}

	useUserStore.getState().userLogout();
	if (legacy) clearLegacyToken();
}
